import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  AlertTriangle,
  Clock,
  FileText,
  ExternalLink,
  Upload,
  Truck,
  Ship,
  Plane,
  Train,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader, MetricCard } from '@/components/common';
import {
  rateAnalyticsApi,
  RateAnalyticsSummary,
  ExpiringRate,
  RateActivityPoint,
  TopCarrier,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';

// Chart colors
const MODE_COLORS: Record<TransportMode, string> = {
  TruckFTL: '#3B82F6',
  TruckLTL: '#60A5FA',
  Ocean: '#10B981',
  Air: '#8B5CF6',
  Rail: '#F59E0B',
  Intermodal: '#6366F1',
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#6366F1', '#EC4899', '#14B8A6'];
const BAR_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6'];

const MODE_ICONS: Record<TransportMode, React.ReactNode> = {
  TruckFTL: <Truck className="w-4 h-4" />,
  TruckLTL: <Truck className="w-4 h-4" />,
  Ocean: <Ship className="w-4 h-4" />,
  Air: <Plane className="w-4 h-4" />,
  Rail: <Train className="w-4 h-4" />,
  Intermodal: <Package className="w-4 h-4" />,
};

export default function RateDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<RateAnalyticsSummary | null>(null);
  const [expiringRates, setExpiringRates] = useState<ExpiringRate[]>([]);
  const [activity, setActivity] = useState<RateActivityPoint[]>([]);
  const [topCarriers, setTopCarriers] = useState<TopCarrier[]>([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Helper to safely call API and return null on error
      const safeCall = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
        try {
          return await fn();
        } catch (error) {
          // Silently handle 404 errors (backend not implemented yet)
          const errorMessage = getErrorMessage(error);
          if (!errorMessage.includes('404') && !errorMessage.includes('Not Found') && !errorMessage.includes('Cannot GET')) {
            console.error('API error:', errorMessage);
          }
          return null;
        }
      };

      const [summaryData, expiringData, activityData, carriersData] = await Promise.all([
        safeCall(() => rateAnalyticsApi.getSummary()),
        safeCall(() => rateAnalyticsApi.getExpiring(30)),
        safeCall(() => rateAnalyticsApi.getActivity(30)),
        safeCall(() => rateAnalyticsApi.getTopCarriers(5)),
      ]);

      if (summaryData) setSummary(summaryData);
      if (expiringData) setExpiringRates(expiringData);
      if (activityData) setActivity(activityData);
      if (carriersData) setTopCarriers(carriersData);

      setLoading(false);
    };

    loadData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader title="Rate Dashboard" description="Loading rate analytics..." />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Rate Dashboard"
        description="Overview of rate management and analytics"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/tms/rate-import')}>
              <Upload className="w-4 h-4 mr-2" />
              Import Rates
            </Button>
            <Button onClick={() => navigate('/tms/rates')}>
              <FileText className="w-4 h-4 mr-2" />
              Manage Rates
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Active Rates"
          value={formatNumber(summary?.activeRates || 0)}
          icon={<FileText className="w-5 h-5 text-emerald-500" />}
          trend={
            summary?.ratesTrend
              ? {
                  direction: summary.ratesTrend > 0 ? 'up' : summary.ratesTrend < 0 ? 'down' : 'neutral',
                  value: `${Math.abs(summary.ratesTrend)}%`,
                  text: 'this month',
                }
              : undefined
          }
        />
        <MetricCard
          label="Total Rates"
          value={formatNumber(summary?.totalRates || 0)}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <MetricCard
          label="Contract Rates"
          value={formatNumber(summary?.contractRates || 0)}
          icon={<FileText className="w-5 h-5 text-blue-500" />}
        />
        <MetricCard
          label="Expiring Soon"
          value={formatNumber(summary?.expiringRates || 0)}
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
          trend={{ direction: 'neutral', value: '30', text: 'days' }}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rates by Mode Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Rates by Mode</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.byMode && summary.byMode.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.byMode}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="mode"
                      label={({ mode, count }) => `${mode}: ${count}`}
                      labelLine={false}
                    >
                      {summary.byMode.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MODE_COLORS[entry.mode] || PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [formatNumber(value), name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rates by Category Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Rates by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.byCategory && summary.byCategory.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.byCategory} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="category"
                      type="category"
                      width={80}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip formatter={(value: number) => [formatNumber(value), 'Rates']} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {summary.byCategory.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Rate Activity (30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={activity}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="created"
                      name="Created"
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="expired"
                      name="Expired"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="updated"
                      name="Updated"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No activity data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Expiring Rates */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              Expiring Rates
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tms/rate-analytics?tab=expiring')}
            >
              View All
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {expiringRates.length > 0 ? (
              <div className="space-y-3">
                {expiringRates.slice(0, 5).map((rate) => (
                  <div
                    key={rate.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="font-medium text-sm">{rate.sku}</div>
                      <div className="text-xs text-muted-foreground">
                        {rate.carrier.name} • {rate.originPort} → {rate.destinationPort}
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="font-semibold">
                        {formatCurrency(rate.baseRate)} {rate.currency}
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          rate.daysUntilExpiry <= 7
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : rate.daysUntilExpiry <= 14
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }
                      >
                        {rate.daysUntilExpiry}d left
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No rates expiring in the next 30 days</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Carriers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-500" />
              Top Carriers by Rates
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tms/rate-analytics?tab=carrier')}
            >
              View All
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {topCarriers.length > 0 ? (
              <div className="space-y-3">
                {topCarriers.map((carrier, idx) => (
                  <div
                    key={carrier.carrierId}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{carrier.carrierName}</div>
                        <div className="text-xs text-muted-foreground">{carrier.carrierCode}</div>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="font-semibold">{formatNumber(carrier.rateCount)} rates</div>
                      <div className="text-xs text-muted-foreground">
                        Avg: {formatCurrency(carrier.avgRate)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No carrier data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Mode Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {(summary?.byMode || []).map((mode) => (
              <div key={mode.mode} className="p-4 bg-muted/30 rounded-lg text-center">
                <div className="flex items-center justify-center mb-2">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${MODE_COLORS[mode.mode]}20` }}
                  >
                    {MODE_ICONS[mode.mode]}
                  </div>
                </div>
                <div className="text-2xl font-bold">{formatNumber(mode.count)}</div>
                <div className="text-sm text-muted-foreground">{mode.mode}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Avg: {formatCurrency(mode.avgRate)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
